import { useUser } from "contexts/UserContext";
import { EnhancedUser } from "services/users/utils";
import BarIcon from "images/bar_chart.svg?react";
import GlobalIcon from "images/global.svg?react";
import HelpOutlinedIcon from "images/help_outlined_icon.svg?react";
import HomeIcon from "images/home.svg?react";
import LearningsIcon from "images/learnings_nav_icon.svg?react";
import LibraryIcon from "images/library_nav_icon.svg?react";
import SettingsOutlinedIcon from "images/settings_outlined_icon.svg?react";
import { WEBSITE_ROUTES, routes } from "pages/routes";
import { ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";

export type Tab = {
  name: string;
  title: string;
  path?: string;
  icon?: ReactElement;
  asIcon?: boolean;
  absolutePath?: string;
  external?: boolean;
  newTab?: boolean;
  onClick?: () => void;
  permissions: (user: EnhancedUser | null | undefined) => boolean | undefined;
};

export function useTabs() {
  const { t } = useTranslation();
  const user = useUser(false);

  return useMemo(() => {
    const isEndUser = (usr?: EnhancedUser | null): usr is EnhancedUser =>
      usr?.isTeacher === true || usr?.isStudent === true;

    const canUseAdaptiveTest = (usr?: EnhancedUser | null): usr is EnhancedUser =>
      usr?.student?.access.adaptive_test === true;

    const textTabs: Tab[] = [
      {
        name: "team_dashboard",
        title: t("Common.nav.companyDashboard"),
        path: routes.teamDashboard.path(),
        permissions: usr => usr?.isTeamManager || usr?.isCompanyManager,
      },
      {
        name: "studentDashboard",
        title: t("Common.nav.myDashboard"),
        path: routes.studentDashboard.path(),
        icon: <HomeIcon />,
        permissions: usr => usr?.isStudent && usr.student?.has_pro_access,
      },
      {
        name: "teacherDashboard",
        title: t("Common.nav.myDashboard"),
        path: routes.teacherDashboard.path(),
        icon: <HomeIcon />,
        permissions: usr => usr?.isTeacher,
      },
      {
        name: "admin_dashboard",
        title: t("Common.nav.adminDashboard"),
        path: routes.admin.root.path(),
        permissions: usr => usr?.isAdmin || usr?.isContentAdmin,
      },
      {
        name: "exercises",
        title: t("Common.labels.exercises"),
        path: routes.exercises.path(),
        permissions: usr => usr?.isTeacher,
      },
      {
        name: "myProgress",
        title: t("Common.nav.myProgress"),
        path: routes.myProgress.path(),
        icon: <LearningsIcon />,
        permissions: usr => usr?.student?.has_pro_access,
      },
      {
        name: "teacher_resources",
        title: t("Common.nav.teacherHub"),
        absolutePath: WEBSITE_ROUTES.teacherHub,
        external: true,
        permissions: usr => usr?.isTeacher,
      },
      {
        name: "library",
        title: t("Common.labels.catalog"),
        path: routes.libraryRoot.path(),
        icon: <LibraryIcon />,
        permissions: usr =>
          usr?.isContentAdmin ||
          usr?.isAdmin ||
          usr?.isTeacher ||
          usr?.student?.has_pro_access,
      },
      {
        name: "content",
        title: t("Common.nav.content"),
        path: routes.admin.contentFolder.path(),
        permissions: usr => usr?.isAdmin || usr?.isContentAdmin,
      },
      {
        name: "vocabulary",
        title: t("Common.nav.myVocabulary"),
        path: routes.vocabulary.path(),
        permissions: usr => usr?.canUseDictionaryTools,
      },
      {
        name: "admin_settings",
        title: t("Common.nav.settings"),
        path: routes.admin.settings.path(),
        permissions: usr => usr?.isAdmin && !usr.isContentAdmin,
      },
      {
        name: "sign_in",
        title: t("Auth.nav.signIn"),
        path: routes.public.signIn.path(),
        permissions: usr => !usr,
      },
      {
        name: "students",
        title: t("Settings.sections.students"),
        path: routes.students.path(),
        permissions: usr => usr?.isTeacher,
      },
    ];

    const iconTabs: Tab[] = [
      {
        name: "settings",
        title: t("Common.nav.accountSettings"),
        path: routes.settings.path(),
        asIcon: true,
        icon: <SettingsOutlinedIcon />,
        permissions: isEndUser,
      },
      {
        name: "levelTest",
        title: t("Common.nav.takeLevelTest"),
        path: routes.adaptiveTest.path(),
        asIcon: true,
        icon: <BarIcon />,
        permissions: usr =>
          canUseAdaptiveTest(usr) && usr.student?.adaptive_test?.status !== "completed",
      },
      {
        name: "retakeLevelTest",
        title: t("Common.nav.retakeLevelTest"),
        path: routes.adaptiveTest.path(),
        asIcon: true,
        icon: <BarIcon />,
        permissions: usr =>
          canUseAdaptiveTest(usr) && usr.student?.adaptive_test?.status === "completed",
      },
      {
        name: "help",
        title: t("Common.nav.helpCenter"),
        absolutePath: WEBSITE_ROUTES.help,
        external: true,
        asIcon: true,
        newTab: true,
        icon: <HelpOutlinedIcon />,
        permissions: isEndUser,
      },
      {
        name: "language",
        title: t("Common.nav.language"),
        asIcon: true,
        icon: <GlobalIcon />,
        permissions: isEndUser,
      },
    ];

    return {
      textTabs: textTabs.filter(tab => tab.permissions(user)),
      iconTabs: iconTabs.filter(tab => tab.permissions(user)),
    };
  }, [user, t]);
}
